import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { Copy, Gift, Loader2, Mail, MessageCircle, Share2, Sparkles, Wallet } from "lucide-react";
import { Header } from "@/components/site/Header";
import { Footer } from "@/components/site/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { getMyReferralSummary, type ReferralSummary } from "@/lib/referrals.functions";
import { formatGBP } from "@/lib/pricing";

export const Route = createFileRoute("/referrals")({
  head: () => ({
    meta: [
      { title: "Refer & earn — Peopels.com" },
      {
        name: "description",
        content:
          "Invite friends to Peopels.com and you both get credit when they complete their first booking with a trusted London Helper.",
      },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: ReferralsPage,
});

function ReferralsPage() {
  const navigate = useNavigate();
  const fetchSummary = useServerFn(getMyReferralSummary);
  const [summary, setSummary] = useState<ReferralSummary | null>(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const data = await fetchSummary();
      setSummary(data);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Couldn't load your referrals");
    } finally {
      setLoading(false);
    }
  }, [fetchSummary]);

  useEffect(() => {
    let mounted = true;
    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      if (!data.session) {
        navigate({ to: "/auth", search: { redirect: "/referrals" } });
        return;
      }
      void load();
    });
    return () => {
      mounted = false;
    };
  }, [load, navigate]);

  const copy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Copied");
    } catch {
      toast.error("Couldn't copy — please copy manually");
    }
  };

  const share = async () => {
    if (!summary) return;
    const text = `Get a trusted London Helper on Peopels.com — use my code ${summary.code} for £${summary.referee_reward} off your first booking.`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "Peopels.com", text, url: summary.share_url });
      } catch {
        /* user cancelled */
      }
    } else {
      await copy(`${text} ${summary.share_url}`);
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <Header />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6 sm:py-12">
        <nav className="mb-4 text-sm text-muted-foreground">
          <Link to="/dashboard" className="hover:text-foreground">
            Dashboard
          </Link>
          <span className="px-2">/</span>
          <span className="text-foreground">Refer & earn</span>
        </nav>

        <header className="mb-8">
          <h1 className="font-display text-3xl font-bold sm:text-4xl">Refer a friend, both get credit</h1>
          <p className="mt-2 max-w-2xl text-muted-foreground">
            Share your code with friends across Greater London. When they complete their first booking,
            you get <strong>£{summary?.referrer_reward ?? 15}</strong> in credit and they get{" "}
            <strong>£{summary?.referee_reward ?? 10}</strong> off.
          </p>
        </header>

        {loading || !summary ? (
          <div className="flex h-40 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid gap-6 lg:grid-cols-3">
            <Card className="lg:col-span-2 overflow-hidden border-primary/20">
              <div className="bg-gradient-to-br from-primary/10 via-background to-background p-6 sm:p-8">
                <div className="flex items-center gap-2 text-sm font-medium text-primary">
                  <Gift className="h-4 w-4" /> Your referral code
                </div>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <code className="rounded-lg bg-foreground px-4 py-2 font-mono text-2xl font-bold tracking-widest text-background">
                    {summary.code}
                  </code>
                  <Button onClick={() => copy(summary.code)} variant="outline" size="sm">
                    <Copy className="mr-1.5 h-4 w-4" /> Copy code
                  </Button>
                </div>

                <div className="mt-6 space-y-2">
                  <label className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    Share link
                  </label>
                  <div className="flex flex-col gap-2 sm:flex-row">
                    <Input
                      readOnly
                      value={summary.share_url}
                      onFocus={(e) => e.currentTarget.select()}
                      className="font-mono text-sm"
                    />
                    <Button onClick={() => copy(summary.share_url)} variant="secondary">
                      <Copy className="mr-1.5 h-4 w-4" /> Copy link
                    </Button>
                  </div>
                </div>

                <div className="mt-5 flex flex-wrap gap-2">
                  <Button onClick={share} className="gap-1.5">
                    <Share2 className="h-4 w-4" /> Share
                  </Button>
                  <Button asChild variant="outline" className="gap-1.5">
                    <a
                      href={`https://wa.me/?text=${encodeURIComponent(
                        `Get a trusted London Helper on Peopels.com — use my code ${summary.code} for £${summary.referee_reward} off your first booking. ${summary.share_url}`,
                      )}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      <MessageCircle className="h-4 w-4" /> WhatsApp
                    </a>
                  </Button>
                  <Button asChild variant="outline" className="gap-1.5">
                    <a
                      href={`mailto:?subject=${encodeURIComponent("Try Peopels.com — £" + summary.referee_reward + " off your first booking")}&body=${encodeURIComponent(
                        `I've been using Peopels.com for trusted local Helpers across London. Use my code ${summary.code} for £${summary.referee_reward} off your first booking: ${summary.share_url}`,
                      )}`}
                    >
                      <Mail className="h-4 w-4" /> Email
                    </a>
                  </Button>
                </div>
              </div>
            </Card>

            <Card className="p-6">
              <div className="flex items-center gap-2 text-sm font-medium text-muted-foreground">
                <Wallet className="h-4 w-4" /> Available credit
              </div>
              <div className="mt-2 font-display text-4xl font-bold tabular-nums">
                {formatGBP(summary.credit_balance)}
              </div>
              <p className="mt-3 text-sm text-muted-foreground">
                Applied automatically at checkout on your next eligible booking.
              </p>
              <div className="mt-4 rounded-lg bg-muted/60 p-3 text-xs text-muted-foreground">
                <Sparkles className="mb-1 inline h-3.5 w-3.5 text-primary" /> Credit never expires and stacks
                across multiple referrals.
              </div>
            </Card>

            <Card className="p-6 lg:col-span-2">
              <h2 className="font-display text-lg font-semibold">Your referrals</h2>
              {summary.referrals.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  No referrals yet. Share your code above to get started — you'll earn credit when each
                  friend completes their first booking.
                </p>
              ) : (
                <ul className="mt-4 divide-y divide-border">
                  {summary.referrals.map((r) => (
                    <li key={r.id} className="flex items-center justify-between gap-3 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium">
                          {r.referred_name || r.referred_email || "A friend"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(r.created_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        {r.status === "completed" ? (
                          <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                            +{formatGBP(r.referrer_reward ?? 0)}
                          </Badge>
                        ) : (
                          <Badge variant="secondary">Signed up</Badge>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-6">
              <h2 className="font-display text-lg font-semibold">Credit history</h2>
              {summary.ledger.length === 0 ? (
                <p className="mt-3 text-sm text-muted-foreground">
                  Your credit movements will appear here.
                </p>
              ) : (
                <ul className="mt-4 space-y-2 text-sm">
                  {summary.ledger.map((row) => (
                    <li key={row.id} className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <p className="truncate text-xs font-medium capitalize text-foreground">
                          {row.reason.replace(/_/g, " ")}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {new Date(row.created_at).toLocaleDateString("en-GB", {
                            day: "numeric",
                            month: "short",
                          })}
                        </p>
                      </div>
                      <span
                        className={`text-sm font-semibold tabular-nums ${
                          row.points >= 0 ? "text-emerald-600" : "text-foreground"
                        }`}
                      >
                        {row.points >= 0 ? "+" : ""}
                        {formatGBP(row.points)}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </Card>

            <Card className="p-6 lg:col-span-3">
              <h2 className="font-display text-lg font-semibold">How it works</h2>
              <ol className="mt-4 grid gap-4 sm:grid-cols-3">
                <li className="rounded-lg border border-border/60 bg-card p-4">
                  <div className="font-display text-2xl font-bold text-primary">1</div>
                  <p className="mt-1 text-sm font-medium">Share your code</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Send your link to friends in Greater London.
                  </p>
                </li>
                <li className="rounded-lg border border-border/60 bg-card p-4">
                  <div className="font-display text-2xl font-bold text-primary">2</div>
                  <p className="mt-1 text-sm font-medium">They book a Helper</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Your friend gets £{summary.referee_reward} off their first job.
                  </p>
                </li>
                <li className="rounded-lg border border-border/60 bg-card p-4">
                  <div className="font-display text-2xl font-bold text-primary">3</div>
                  <p className="mt-1 text-sm font-medium">You both earn credit</p>
                  <p className="mt-1 text-xs text-muted-foreground">
                    You get £{summary.referrer_reward} credit once their booking completes.
                  </p>
                </li>
              </ol>
            </Card>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}
